import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import CustomRadioButton from '../../uiParts/form/CustomRadioButton';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { updateEmailInboxSettings } from '../../../api/firebase/function/emailInboxFunction';
import { EmailInbox } from '../../../types/emailImport';
import { EMAIL_INBOX_NAME_MAX_LENGTH, fileProcessingType } from '../../../constants/enum';
import { isValidSender, parseSenders } from '../../../utils/emailSender';
import {
  gray40,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogTitle, regularSm } from '../../../constants/location';

interface Props {
  open: boolean;
  inbox: EmailInbox | null;
  onClose: () => void;
  /** 保存成功時（親側で一覧を再読み込みする） */
  onSaved: () => void;
  /** 操作結果の通知（スナックバーはページ側で表示） */
  onNotify: (message: string, isError?: boolean) => void;
}

// design-rules.md「8. ダイアログ」に準拠
const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '560px',
      maxWidth: 'calc(100% - 32px)',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  dialogTitle,
  addressRow: {
    ...regularSm,
    color: gray80,
    marginBottom: '20px',
    wordBreak: 'break-all' as const,
  },
  addressValue: {
    color: gray100,
  },
  errorText: {
    ...regularSm,
    color: red50,
    marginBottom: '12px',
  },
  // 許可送信元フィールドの補足（空欄時の挙動を説明）
  hintText: {
    ...regularSm,
    color: gray80,
    marginTop: '4px',
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  // CustomRadioButton の root margin(24px)＋空ヘルパーで余白が広がるため、
  // 空ヘルパーを隠し margin を相殺して他フィールド（gap:16px）と揃える。
  radioWrapper: {
    marginBottom: '-24px',
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

/**
 * 専用アドレスの編集ダイアログ（#3042）。
 * 発行済みアドレスはそのままに、名称（論理名）と許可送信元（allowlist）を編集する。
 */
const EmailInboxEditDialog: React.FC<Props> = ({ open, inbox, onClose, onSaved, onNotify }) => {
  const { companyId } = useCompanyId();
  const proxyDialogSx = useProxyDialogSx();
  const [nameText, setNameText] = useState('');
  const [nameError, setNameError] = useState('');
  const [sendersText, setSendersText] = useState('');
  const [sendersError, setSendersError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  // 出力方式（ファイル処理方式）。既存値を復元し、未設定は pagePerResult とする。
  const [outputType, setOutputType] = useState<number>(fileProcessingType.pagePerResult);

  // ダイアログを開くたびに対象アドレスの現在値で初期化する
  useEffect(() => {
    if (open && inbox) {
      setNameText(inbox.name ?? '');
      setSendersText((inbox.allowed_senders ?? []).join('\n'));
      setOutputType(inbox.file_type ?? fileProcessingType.pagePerResult);
      setNameError('');
      setSendersError('');
      setSaveError('');
    }
  }, [open, inbox]);

  const handleSave = async (): Promise<void> => {
    if (!companyId || !inbox) return;
    let hasError = false;
    const trimmedName = nameText.trim();
    if (!trimmedName) {
      setNameError('名称を入力してください。');
      hasError = true;
    } else if (trimmedName.length > EMAIL_INBOX_NAME_MAX_LENGTH) {
      setNameError(`名称は${EMAIL_INBOX_NAME_MAX_LENGTH}文字以内で入力してください。`);
      hasError = true;
    }
    // allowlist は任意入力。入力がある場合のみ形式チェックする（空欄は全送信元許可を意味する）
    const senders = parseSenders(sendersText);
    if (senders.length > 0 && senders.some(s => !isValidSender(s))) {
      setSendersError(
        'メールアドレスまたはドメインの形式で入力してください（例: tanaka@example.co.jp / example.co.jp）',
      );
      hasError = true;
    }
    if (hasError) return;

    setSaving(true);
    setSaveError('');
    try {
      await updateEmailInboxSettings({
        company_id: companyId,
        inbox_id: inbox.id,
        name: trimmedName,
        allowed_senders: senders,
        file_type: outputType,
      });
      onNotify('アドレスを更新しました。');
      onSaved();
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setSaveError('更新に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(): void => {
        if (!saving) onClose();
      }}
      maxWidth={false}
      sx={[styles.dialog, proxyDialogSx]}
    >
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>アドレスを編集</Typography>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography sx={styles.addressRow}>
        対象アドレス：
        <Box component="span" sx={styles.addressValue}>
          {inbox?.address}
        </Box>
      </Typography>

      {saveError && <Typography sx={styles.errorText}>{saveError}</Typography>}

      <Box sx={styles.fieldWrapper}>
        <CustomTextfield
          labelText="名称（一覧で識別するための名称）"
          requiredText="必須"
          value={nameText}
          onChange={(e): void => {
            setNameText(e.target.value);
            if (nameError) setNameError('');
          }}
          error={Boolean(nameError)}
          helperText={nameError}
          placeholder="A社 請求書 など"
          inputWidth="100%"
          fontSizeIs14px
          formMb="0"
        />
        {/* 出力方式（ファイルアップロードダイアログと同じ CustomRadioButton） */}
        <Box sx={styles.radioWrapper}>
          <CustomRadioButton
            value={String(outputType)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
              setOutputType(Number(event.target.value));
            }}
            labelText="ファイル処理方式"
            required={false}
            firstOption="1ページごとに出力"
            secondOption="複数ページをまとめて出力"
            firstOptionDescription="ページごとに分割してOCR処理"
            secondOptionDescription="複数ページを統合してOCR処理"
            firstValue={fileProcessingType.pagePerResult}
            secondValue={fileProcessingType.singleResult}
          />
        </Box>
        <Box>
          <CustomTextfield
            labelText="許可送信元（allowlist・1行に1件。メールアドレス または ドメイン）"
            value={sendersText}
            onChange={(e): void => {
              setSendersText(e.target.value);
              if (sendersError) setSendersError('');
            }}
            error={Boolean(sendersError)}
            helperText={sendersError}
            placeholder={'tanaka@example.co.jp\nexample.co.jp'}
            multiline
            minRows={3}
            inputWidth="100%"
            fontSizeIs14px
            formMb="0"
          />
          <Typography sx={styles.hintText}>
            空欄の場合、すべての送信元からのメールを受け付けます。特定の相手だけに限定したい場合は入力してください。
            ※なりすましなど不正なメールは自動的に除外されます。
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.actions}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
        >
          キャンセル
        </Button>
        <Button
          onClick={(): void => void handleSave()}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={[primaryButtonBackgroundColor, styles.actionButton]}
        >
          保存する
        </Button>
      </Box>
    </Dialog>
  );
};

export default EmailInboxEditDialog;
